import React from 'react';
import { Search, X } from 'lucide-react';
import { getMemberDisplayName, getMemberAvatarUrl } from '../../utils/communityConstants';

const CommunityMembersModal = ({
  isOpen,
  onClose,
  members,
  searchQuery,
  onSearchChange,
  onSendMessage,
}) => {
  if (!isOpen) return null;

  const inputClass =
    'w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 pl-10 pr-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent';

  return (
    <div className="fixed inset-0 z-50 overflow-auto bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div
        className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-xl w-full max-w-4xl mx-auto overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="members-modal-title"
      >
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-800">
          <div className="flex-1" />
          <h3
            id="members-modal-title"
            className="text-xl font-semibold flex-1 text-center text-gray-900 dark:text-white"
          >
            All Members
          </h3>
          <div className="flex-1 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-800 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search members by name, email, or company..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>

        <div className="px-6 py-4">
          {members.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400">
                No members found matching your search.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto">
              {members.map((member) => {
                const displayName = getMemberDisplayName(member);
                return (
                  <div
                    key={`${member.accountId}-${member.id}`}
                    className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <img
                      src={getMemberAvatarUrl(member, 48)}
                      alt={`${displayName} avatar`}
                      className="w-11 h-11 rounded-full object-cover shrink-0"
                      onError={(e) => {
                        e.target.src = getMemberAvatarUrl({ ...member, photoURL: null }, 48);
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm truncate text-gray-900 dark:text-white">
                        {displayName}
                      </h4>
                      <p className="text-xs truncate text-gray-500 dark:text-gray-400">
                        {member.company || 'No company'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => onSendMessage?.(member)}
                      className="text-xs font-medium px-2.5 py-1 rounded-full text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors shrink-0"
                    >
                      Message
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommunityMembersModal;
